import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Sparkles,
  Trash2,
  Paperclip,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AIMessageBubble } from "./ai-message-bubble";
import { AIAssistantInput } from "./ai-assistant-input";
import {
  MAX_AI_FORM_ASSIST_ATTACHMENTS,
  type AIAttachment,
  type AIMessage,
} from "@/hooks/use-ai-form-assistant";

interface AIAssistantCardProps {
  messages: AIMessage[];
  onSendMessage: (text: string) => void;
  isLoading: boolean;
  onClearConversation: () => void;
  /** Title shown in the header. */
  title?: string;
  /** Subtitle shown below the title when no messages yet. */
  emptyStatePrompt?: string;
  /** Placeholder shown in the input when empty. */
  inputPlaceholder?: string;
  /** Starter prompt suggestions shown as chips when no messages yet. */
  starterPrompts?: string[];
  /** Attachments queued for the next message. Pass to enable file uploads. */
  pendingAttachments?: AIAttachment[];
  onAddAttachment?: (file: File) => Promise<void>;
  onRemoveAttachment?: (tempId: string) => void;
  /** True if any attachment has a validation error — disables send. */
  hasInvalidAttachment?: boolean;
}

const ACCEPT_MIME =
  "application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp";

const ALLOWED_MIME_SET = new Set(ACCEPT_MIME.split(","));

function isAllowedFile(file: File): boolean {
  // Some browsers don't set the mime type (e.g. .heic). Fall back to extension.
  if (file.type && ALLOWED_MIME_SET.has(file.type)) {
    return true;
  }
  const ext = file.name.toLowerCase().split(".").pop();
  return (
    !!ext && ["pdf", "jpg", "jpeg", "png", "heic", "heif", "webp"].includes(ext)
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: AIAttachment;
  onRemove?: (tempId: string) => void;
}) {
  const isImage = attachment.mimeType.startsWith("image/");
  const Icon = isImage ? ImageIcon : FileText;
  const hasError = !!attachment.errorMessage;

  return (
    <div
      className={`group flex min-w-0 w-full max-w-full items-center gap-2 rounded-xl border p-2 text-xs sm:w-auto ${
        hasError
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-slate-200 bg-white text-slate-700"
      }`}
      title={attachment.errorMessage ?? attachment.fileName}
    >
      {hasError ? (
        <AlertCircle
          size={16}
          className="text-red-500 shrink-0"
          aria-hidden="true"
        />
      ) : (
        <Icon size={16} className="text-primary shrink-0" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1 sm:max-w-48">
        <p className="truncate font-medium">{attachment.fileName}</p>
        <p className="mt-0.5 text-slate-500">
          {formatFileSize(attachment.fileSizeBytes)}
        </p>
        {hasError && (
          <p className="mt-1 break-words leading-relaxed text-red-700">
            {attachment.errorMessage}
          </p>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(attachment.tempId)}
          className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary sm:size-9"
          aria-label="Remove attachment"
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export function AIAssistantCard({
  messages,
  onSendMessage,
  isLoading,
  onClearConversation,
  title = "Build with AI",
  emptyStatePrompt = "Describe what you want to build and I'll set it up for you. You can always edit manually below.",
  inputPlaceholder = "Describe what you want to build...",
  starterPrompts = [],
  pendingAttachments,
  onAddAttachment,
  onRemoveAttachment,
  hasInvalidAttachment = false,
}: AIAssistantCardProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const hasMessages = messages.length > 0;
  const attachmentsEnabled = !!onAddAttachment;
  const pendingAttachmentCount = pendingAttachments?.length ?? 0;
  const canAddAttachments =
    attachmentsEnabled &&
    pendingAttachmentCount < MAX_AI_FORM_ASSIST_ATTACHMENTS;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const ingestFiles = useCallback(
    async (files: File[]) => {
      if (!onAddAttachment) {
        return;
      }
      setAttachmentError(null);

      const allowedFiles = files.filter(isAllowedFile);
      const slotsAvailable =
        MAX_AI_FORM_ASSIST_ATTACHMENTS - pendingAttachmentCount;

      if (slotsAvailable <= 0) {
        setAttachmentError(
          `You can attach up to ${MAX_AI_FORM_ASSIST_ATTACHMENTS} files per message.`,
        );
        return;
      }

      const filesToAdd = allowedFiles.slice(0, slotsAvailable);
      if (allowedFiles.length > slotsAvailable) {
        setAttachmentError(
          `You can attach up to ${MAX_AI_FORM_ASSIST_ATTACHMENTS} files per message. Some files were skipped.`,
        );
      }

      for (const file of filesToAdd) {
        try {
          await onAddAttachment(file);
        } catch (error) {
          setAttachmentError(
            error instanceof Error
              ? error.message
              : "Couldn't attach that file. Please try again.",
          );
          break;
        }
      }
    },
    [onAddAttachment, pendingAttachmentCount],
  );

  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file
    await ingestFiles(files);
  };

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!attachmentsEnabled) {
        return;
      }
      if (!e.dataTransfer.types.includes("Files")) {
        return;
      }
      e.preventDefault();
      dragCounterRef.current += 1;
      setIsDraggingOver(true);
    },
    [attachmentsEnabled],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!attachmentsEnabled) {
        return;
      }
      e.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDraggingOver(false);
      }
    },
    [attachmentsEnabled],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!attachmentsEnabled) {
        return;
      }
      if (!e.dataTransfer.types.includes("Files")) {
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    [attachmentsEnabled],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      if (!attachmentsEnabled) {
        return;
      }
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
      const files = Array.from(e.dataTransfer.files ?? []);
      await ingestFiles(files);
    },
    [attachmentsEnabled, ingestFiles],
  );

  return (
    <div
      className={`relative min-w-0 rounded-2xl border bg-white shadow-xs overflow-hidden transition-colors ${
        isDraggingOver
          ? "border-primary ring-2 ring-primary/15 ring-offset-1"
          : "border-slate-200"
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDraggingOver && attachmentsEnabled && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-primary/5 p-4 backdrop-blur-[1px]">
          <div className="flex min-w-0 max-w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-white/90 px-4 py-4 text-center shadow-lg sm:px-6">
            <Paperclip size={22} className="text-primary" />
            <p className="text-sm font-medium text-primary">
              Drop files to attach
            </p>
            <p className="text-xs text-slate-500">PDF or image</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-3 sm:items-center sm:px-5">
        <div className="flex min-w-0 flex-1 items-start gap-2.5 sm:items-center">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary sm:size-10">
            <Sparkles size={16} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h3 className="break-words text-sm font-semibold text-slate-900 leading-5">
              {title}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 leading-4">
              Or edit manually below
            </p>
          </div>
        </div>
        {hasMessages && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="size-12 shrink-0 gap-1.5 rounded-xl p-0 text-slate-500 hover:bg-red-50 hover:text-red-700 sm:h-9 sm:w-auto sm:px-2 sm:text-xs"
            onClick={onClearConversation}
            aria-label="Clear conversation"
            title="Clear conversation"
          >
            <Trash2 size={16} aria-hidden="true" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
        )}
      </div>

      {/* Empty state intro */}
      {!hasMessages && (
        <div className="px-4 sm:px-5 pb-3">
          <p className="text-sm leading-relaxed text-slate-600">
            {emptyStatePrompt}
          </p>
        </div>
      )}

      {/* Conversation */}
      {hasMessages && (
        <div
          ref={scrollRef}
          className="min-w-0 px-4 sm:px-5 py-3 max-h-[280px] overflow-y-auto space-y-3 bg-slate-50 border-y border-slate-200"
        >
          {messages.map((msg) => (
            <AIMessageBubble key={msg.id} message={msg} />
          ))}
          {isLoading && (
            <div className="flex gap-2">
              <div className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                <Sparkles size={12} />
              </div>
              <div className="bg-slate-100 rounded-lg px-3 py-1.5">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.1s]" />
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending attachment chips (above input) */}
      {attachmentsEnabled &&
        pendingAttachments &&
        pendingAttachments.length > 0 && (
          <div className="px-4 sm:px-5 pt-3 flex min-w-0 flex-wrap gap-2">
            {pendingAttachments.map((att) => (
              <AttachmentChip
                key={att.tempId}
                attachment={att}
                onRemove={onRemoveAttachment}
              />
            ))}
          </div>
        )}

      {/* Composer with optional attachment action */}
      <div className="min-w-0 px-4 sm:px-5 py-3">
        {attachmentsEnabled && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_MIME}
            className="hidden"
            onChange={handleFilePick}
          />
        )}
        <AIAssistantInput
          onSend={onSendMessage}
          isLoading={isLoading || hasInvalidAttachment}
          placeholder={inputPlaceholder}
          attachmentAction={
            attachmentsEnabled ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || !canAddAttachments}
                className="h-12 shrink-0 gap-1.5 rounded-xl px-2 text-sm text-slate-600 hover:bg-primary/5 hover:text-primary"
                aria-label="Attach a file"
                title={
                  canAddAttachments
                    ? "Attach a PDF or image"
                    : `Maximum ${MAX_AI_FORM_ASSIST_ATTACHMENTS} files`
                }
              >
                <Paperclip size={16} aria-hidden="true" />
                Attach file
              </Button>
            ) : undefined
          }
        />
      </div>

      {attachmentsEnabled && attachmentError && (
        <div
          className="px-4 sm:px-5 pb-3 -mt-1 flex items-start gap-2 text-xs leading-relaxed text-red-600"
          role="alert"
        >
          <AlertCircle
            size={14}
            className="mt-0.5 shrink-0"
            aria-hidden="true"
          />
          <span className="min-w-0 break-words">{attachmentError}</span>
        </div>
      )}

      {/* Starter prompts (only when empty) */}
      {!hasMessages && starterPrompts.length > 0 && (
        <div className="px-4 sm:px-5 pb-4">
          <p className="text-xs font-medium text-slate-500 mb-2">
            Try one of these:
          </p>
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-1.5">
            {starterPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="min-h-12 min-w-0 w-full max-w-full whitespace-normal break-words rounded-xl border border-slate-400 bg-white px-3 py-2.5 text-left text-sm leading-5 text-slate-700 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 sm:min-h-9 sm:w-auto sm:py-2 sm:text-xs"
                onClick={() => onSendMessage(prompt)}
                disabled={isLoading}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
